import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ColorPalette, ProposalTemplate, TemplateProduct, TemplateProductPayload } from 'src/common/common.interface'
import { CustomizationAction, CustomizationGroupType, ProposalTemplateType } from 'src/common/enum'
import { templateProductToProductPayload } from 'src/pages/TemplateProduct/helper'
import {
  Customization,
  CustomizationGroup,
  ProductCustomization,
  ProductCustomizationGroup,
  SaveProductCustomizationResult
} from 'src/common/customization.interface'
import { CustomizationGroupTypeLabel } from 'src/common/constants'
import { generateUUID } from '../helper'

export interface ProposalTemplateState {
  templates: ProposalTemplate[]
  templateProducts: TemplateProduct[]
  palettes: ColorPalette[]

  currentTemplate: ProposalTemplate | null
  templateVersion: 'V1' | 'V2'

  openProductCatalog: boolean

  productPayload: TemplateProductPayload
  filteredTemplateType: ProposalTemplateType
  customizationGroups: CustomizationGroup[]
  productCustomizationGroups: ProductCustomizationGroup[]
  productCustomizationSaving: boolean
  customizationSaving: boolean
}

const initialState: ProposalTemplateState = {
  templates: [],
  templateProducts: [],
  currentTemplate: null,
  templateVersion: 'V1',
  palettes: [],
  openProductCatalog: false,
  productPayload: { products: [] },
  filteredTemplateType: ProposalTemplateType.ALL,
  customizationGroups: [],
  productCustomizationGroups: [],
  productCustomizationSaving: false,
  customizationSaving: false
}

const proposalTemplateSlice = createSlice({
  name: 'proposal-template',
  initialState,
  reducers: {
    setProposalTemplates: (state, action: PayloadAction<ProposalTemplate[]>) => {
      state.templates = action.payload
    },
    setTemplateProducts: (state, action: PayloadAction<TemplateProduct[]>) => {
      const templateProducts = action.payload || []
      templateProducts.forEach((tp) => {
        tp.originalIndex = tp.sequenceIndex
      })
      state.templateProducts = templateProducts
      // Set product customization groups
      state.productCustomizationGroups = templateProducts
        ?.map((tp) =>
          tp.product.productCustomizationGroups?.map((pcg) => ({
            ...pcg,
            productId: tp.product?.id,
            type: pcg.customizationGroup.type,
            productCustomizations: pcg.productCustomizations.map((pc) => ({
              ...pc,
              price: pc.price / 100 // convert to dollars
            }))
          }))
        )
        .flat()
        .map((pcg) => ({
          ...pcg,
          productCustomizations: pcg?.productCustomizations?.sort((a, b) => a.customizationId - b.customizationId),
          action: CustomizationAction.NONE
        }))
    },
    updateTemplateProductsIndex: (state, action: PayloadAction<TemplateProduct[]>) => {
      action.payload.forEach((tp) => {
        const index = state.templateProducts.findIndex((p) => p.id === tp.id)
        if (index > -1) {
          state.templateProducts[index].sequenceIndex = tp.sequenceIndex
        }

        // Update payload
        const payloadIndex = state.productPayload.products.findIndex((p) => p.id === tp.id)
        if (payloadIndex > -1) {
          state.productPayload.products[payloadIndex].sequenceIndex = tp.sequenceIndex
        } else {
          state.productPayload.products.push(templateProductToProductPayload(tp, { isSequenceIndexChanged: tp.id > 0 }))
        }
      })
    },
    setProposalTemplate: (state, action: PayloadAction<ProposalTemplate | null>) => {
      state.currentTemplate = action.payload
    },
    setTemplateVersion: (state, action: PayloadAction<'V1' | 'V2'>) => {
      state.templateVersion = action.payload
    },
    setPalettes: (state, action: PayloadAction<ColorPalette[]>) => {
      state.palettes = action.payload
    },
    changeTemplatePalette: (state, action: PayloadAction<ColorPalette>) => {
      if (state.currentTemplate) {
        state.currentTemplate.colorPalette = action.payload
      }
    },
    removeATemplateProduct: (state, action: PayloadAction<number>) => {
      const tempProductId = action.payload
      const index = state.templateProducts.findIndex((tp) => tp.id === tempProductId)
      let oldProduct: TemplateProduct | undefined
      if (index > -1) {
        oldProduct = state.templateProducts[index]
        state.templateProducts.splice(index, 1)
      }

      if (oldProduct) {
        if (tempProductId > 0) {
          const payloadIndex = state.productPayload.products.findIndex((p) => p.id === tempProductId)
          if (payloadIndex > -1) {
            state.productPayload.products[payloadIndex].isDeleted = true
            state.productPayload.products[payloadIndex].isSequenceIndexChanged = false
          } else {
            state.productPayload.products.push({
              id: tempProductId,
              templateId: oldProduct.templateId,
              unitType: oldProduct.unitType,
              productType: oldProduct.productType,
              unitProductType: oldProduct.unitProductType,
              proposalUnitCategory: oldProduct.proposalUnitCategory,
              productId: oldProduct.product.id,
              isDeleted: true
            })
          }
        } else {
          const rIndex = state.productPayload.products.findIndex((p) => p.id === oldProduct?.id)
          if (rIndex > -1) state.productPayload.products.splice(rIndex, 1)
        }
      }
    },
    setOpenProductCatalog: (state, action: PayloadAction<boolean>) => {
      state.openProductCatalog = action.payload
    },
    setProductPayload: (state, action: PayloadAction<TemplateProductPayload>) => {
      state.productPayload.paletteId = action.payload.paletteId
      action.payload.products.forEach((p) => {
        const index = state.productPayload.products.findIndex((tp) => tp.id === p.id)
        if (index > -1) {
          state.productPayload.products[index] = { ...p }
        } else {
          state.productPayload.products.push(p)
        }
      })
    },
    resetAfterSaveChange: (
      state,
      action: PayloadAction<{
        templates: {
          oldId?: number
          newId: number
        }[]
      }>
    ) => {
      state.productPayload = { products: [] }
      action.payload.templates.forEach((p) => {
        const index = state.templateProducts.findIndex((tp) => tp.id === p.oldId)
        if (index > -1) {
          state.templateProducts[index].id = p.newId
        }
      })
      state.templateProducts = state.templateProducts.map((p) => ({
        ...p,
        isPreview: false,
        originalIndex: p.sequenceIndex
      }))
    },
    addSelectedProductsToTemplate: (
      state,
      action: PayloadAction<{ catalogAction: 'ADD_PRODUCT' | 'UPDATE_PRODUCT'; products: TemplateProduct[] }>
    ) => {
      const { products, catalogAction } = action.payload

      if (!state.productPayload) state.productPayload = { products: [] }

      const removeProducts: number[] = []

      if (catalogAction === 'ADD_PRODUCT') {
        state.templateProducts.push(...products.map((p) => ({ ...p, isPreview: true })))
        state.productPayload.products.push(
          ...products.map((p) => ({
            id: p.id,
            templateId: p.templateId,
            unitType: p.unitType,
            productType: p.productType,
            unitProductType: p.unitProductType,
            proposalUnitCategory: p.proposalUnitCategory,
            productId: p.product.id,
            isSelected: false
          }))
        )
      }
      if (catalogAction === 'UPDATE_PRODUCT') {
        products.forEach((p) => {
          const index = state.templateProducts.findIndex((tp) => tp.id === p.id)
          if (index >= 0) {
            removeProducts.push(state.templateProducts[index].product.id)
            state.templateProducts[index] = { ...p, isPreview: true }
          }

          const uIndex = state.productPayload.products.findIndex((pp) => pp.id === p.id)
          if (uIndex >= 0) {
            state.productPayload.products[uIndex] = {
              id: p.id,
              templateId: p.templateId,
              unitType: p.unitType,
              productType: p.productType,
              unitProductType: p.unitProductType,
              proposalUnitCategory: p.proposalUnitCategory,
              productId: p.product.id,
              isSelected: false
            }
          } else {
            state.productPayload.products.push({
              id: p.id,
              templateId: p.templateId,
              unitType: p.unitType,
              productType: p.productType,
              unitProductType: p.unitProductType,
              proposalUnitCategory: p.proposalUnitCategory,
              productId: p.product.id,
              isSelected: false
            })
          }
        })
      }

      // Remove customization groups that are not in the template products
      const newProductCustomizationGroups = state.productCustomizationGroups.filter(
        (pcg) => !removeProducts.includes(pcg.productId)
      )

      // Add new customization groups that come from the selected products
      const customizationGroupsToAdd = products
        ?.map((tp) =>
          tp.product.productCustomizationGroups?.map((pcg) => ({
            ...pcg,
            productId: tp.product?.id,
            type: pcg.customizationGroup.type,
            productCustomizations: pcg.productCustomizations.map((pc) => ({
              ...pc,
              price: pc.price / 100 // convert to dollars
            }))
          }))
        )
        .flat()
        .map((pcg) => ({
          ...pcg,
          productCustomizations: pcg?.productCustomizations?.sort((a, b) => a.customizationId - b.customizationId),
          action: CustomizationAction.NONE
        }))
      state.productCustomizationGroups = [...newProductCustomizationGroups, ...customizationGroupsToAdd]
    },
    toggleTemplateProductIsSelected: (
      state,
      action: PayloadAction<{ templateProductId: number; isSelected: boolean }>
    ) => {
      const { templateProductId, isSelected } = action.payload
      const existingProduct = state.templateProducts.find((tp) => tp.id === templateProductId)
      const existingSelected = state.templateProducts.find(
        (tp) => tp.isSelected && tp.id !== templateProductId && tp.unitType === existingProduct?.unitType
      )
      if (isSelected) {
        // Clear all selected in the same unit type
        state.templateProducts
          .filter((tp) => tp.unitType === existingProduct?.unitType)
          .forEach((tp) => {
            tp.isSelected = false
          })
      }
      if (existingProduct) {
        existingProduct.isSelected = isSelected
        // Update payload
        const payloadIndex = state.productPayload.products.findIndex((p) => p.id === templateProductId)
        if (payloadIndex > -1) {
          state.productPayload.products[payloadIndex].isSelected = isSelected
        } else {
          state.productPayload.products.push(templateProductToProductPayload(existingProduct))
        }
      }
      if (existingSelected) {
        existingSelected.isSelected = false
        // Update payload
        const payloadIndex = state.productPayload.products.findIndex((p) => p.id === existingSelected.id)
        if (payloadIndex > -1) {
          state.productPayload.products[payloadIndex].isSelected = false
        } else {
          state.productPayload.products.push(templateProductToProductPayload(existingSelected))
        }
      }
    },
    toggleSelectedTemplateType: (state, action: PayloadAction<{ type: ProposalTemplateType }>) => {
      state.filteredTemplateType = action.payload.type
    },
    addProductCustomizationGroup: (
      state,
      action: PayloadAction<{ templateProductId: number; customizationGroupType: CustomizationGroupType }>
    ) => {
      const { templateProductId, customizationGroupType } = action.payload
      const existingProduct = state.templateProducts.find((tp) => tp.id === templateProductId)
      if (existingProduct) {
        const existingGroup = state.productCustomizationGroups.find(
          (pcg) => pcg.productId === existingProduct.product?.id && pcg.type === customizationGroupType
        )
        // If this is a deleted group, just bring it back
        if (existingGroup && existingGroup.action === CustomizationAction.DELETE) {
          existingGroup.action = CustomizationAction.UPDATE
          return
        }
        const productCustomizationGroupId = generateUUID()
        const groupToAdd = state.customizationGroups.find((cg) => cg.type === customizationGroupType)
        state.productCustomizationGroups.push({
          id: productCustomizationGroupId,
          productId: existingProduct.product?.id,
          min: 0,
          max: 0,
          name: '',
          type: customizationGroupType,
          customizationGroupId: groupToAdd?.id || -1,
          customizationGroup: groupToAdd || ({} as CustomizationGroup),
          productCustomizations: [
            ...(groupToAdd?.customizations.map(
              (c) =>
                ({
                  id: generateUUID(),
                  price: 0,
                  customizationId: c.id,
                  customization: c,
                  productCustomizationGroupId,
                  isSelected: false
                } as unknown as ProductCustomization)
            ) || [])
          ],
          action: CustomizationAction.CREATE
        })
      }
    },
    setCustomizationGroups: (state, action: PayloadAction<CustomizationGroup[]>) => {
      // Set sequence if it's not set
      action.payload.forEach((cg) => {
        cg.customizations.forEach((c, index) => {
          c.sequence = index
          c.originalSequence = index
        })
      })
      state.customizationGroups = action.payload
    },
    updateProductCustomizationGroup: (
      state,
      action: PayloadAction<{
        productId: number
        data: Partial<ProductCustomizationGroup>
      }>
    ) => {
      const { productId, data } = action.payload
      const existingGroup = state.productCustomizationGroups.find(
        (pcg) => pcg.productId === productId && pcg.type === data.type
      )
      if (existingGroup) {
        existingGroup.min = data.min || existingGroup.min
        existingGroup.max = data.max || existingGroup.max
        existingGroup.name = data.name || existingGroup.name
        existingGroup.action =
          existingGroup.action === CustomizationAction.CREATE ? CustomizationAction.CREATE : CustomizationAction.UPDATE
        data.productCustomizations?.forEach((pc) => {
          const existingCustomization = existingGroup.productCustomizations.find((pcc) => pcc.id === pc.id)
          if (existingCustomization) {
            existingCustomization.price = pc.price
            existingCustomization.isSelected = pc.isSelected
            existingCustomization.isDefault = pc.isDefault
          }
        })
      }
    },
    deleteProductCustomizationGroup: (
      state,
      action: PayloadAction<{ productId: number; data: Partial<ProductCustomizationGroup> }>
    ) => {
      const { productId, data } = action.payload
      const existingGroup = state.productCustomizationGroups.find(
        (pcg) => pcg.productId === productId && pcg.type === data.type
      )
      // If this is a new group, just remove it from the list
      if (existingGroup && existingGroup.action === CustomizationAction.CREATE) {
        state.productCustomizationGroups = state.productCustomizationGroups.filter((pcg) => pcg.id !== existingGroup.id)
      } else if (existingGroup) {
        existingGroup.action = CustomizationAction.DELETE
      }
    },
    updateProductCustomizationFromResponse: (state, action: PayloadAction<SaveProductCustomizationResult[]>) => {
      // Delete all deleted groups
      state.productCustomizationGroups = state.productCustomizationGroups.filter(
        (pcg) => pcg.action !== CustomizationAction.DELETE
      )
      // Update all created/updated groups
      action.payload.forEach((pcr) => {
        const existingGroup = state.productCustomizationGroups.find((pcg) => pcg.id === pcr.id || pcg.id === pcr.tempId)
        if (existingGroup) {
          existingGroup.action = CustomizationAction.NONE
          existingGroup.id = pcr.id
          existingGroup.productCustomizations.forEach((pc) => {
            const existingCustomization = pcr.productCustomizations.find(
              (pcc) => pcc.id === pc.id || pcc.tempId === pc.id
            )
            if (existingCustomization) {
              pc.id = existingCustomization.id
            }
          })
        }
      })
    },
    setProductCustomizationSaving: (state, action: PayloadAction<boolean>) => {
      state.productCustomizationSaving = action.payload
    },
    addCustomizationGroup: (state, action: PayloadAction<{ type: CustomizationGroupType }>) => {
      state.customizationGroups.push({
        id: generateUUID(),
        name: CustomizationGroupTypeLabel[action.payload.type],
        type: action.payload.type,
        customizations: []
      })
    },
    updateCustomization: (
      state,
      action: PayloadAction<{ customizationGroupId: number | string; customization: Partial<Customization> }>
    ) => {
      const existingGroup = state.customizationGroups.find((cg) => cg.id === action.payload.customizationGroupId)
      const existingCustomization = existingGroup?.customizations.find((c) => c.id === action.payload.customization.id)
      if (!existingGroup) return
      if (existingCustomization) {
        existingCustomization.name = action.payload.customization.name || existingCustomization.name
        existingCustomization.color = action.payload.customization.color || existingCustomization.color
        existingCustomization.colorSwatch =
          action.payload.customization.colorSwatch || existingCustomization.colorSwatch
      } else {
        existingGroup.customizations.push({
          id: generateUUID(),
          name: action.payload.customization.name || '',
          sequence: existingGroup.customizations.length,
          originalSequence: existingGroup.customizations.length,
          customizationGroupId: existingGroup.id
        })
      }
    },
    updateCustomizationIndex: (
      state,
      action: PayloadAction<{ groupId: number | string; customizations: Customization[] }>
    ) => {
      const existingGroup = state.customizationGroups.find((cg) => cg.id === action.payload.groupId)
      if (!existingGroup) return
      action.payload.customizations.forEach((c) => {
        const existingCustomization = existingGroup.customizations.find((cc) => cc.id === c.id)
        if (existingCustomization) {
          existingCustomization.sequence = c.sequence
          existingCustomization.originalSequence = c.sequence
        }
      })
      existingGroup.customizations = existingGroup.customizations.sort((a, b) => a.sequence - b.sequence)
    },
    setCustomizationSaving: (state, action: PayloadAction<boolean>) => {
      state.customizationSaving = action.payload
    }
  }
})

export const proposalTemplateAction = proposalTemplateSlice.actions

export default proposalTemplateSlice.reducer
